import type { ReactNode } from 'react';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Readout.css';

export interface ReadoutProps {
  children: ReactNode;
  /**
   * start — a link or anything else that can run to several lines.
   * center — a short fixed-width value, a recovery code.
   */
  align?: 'start' | 'center';
  className?: string;
  testId?: string;
}

/**
 * A value the reader is meant to take away rather than change: a link, a
 * one-time code. It looks like a filled field because it behaves like one —
 * select it, copy it — but there is nothing to type into.
 */
export function Readout({ children, align = 'start', className, testId }: ReadoutProps) {
  return (
    <div
      className={classes('readout', align === 'center' && 'readout--center', className)}
      {...testIdAttr(testId)}
    >
      <Typography as="span" role="meta/mono" className="readout__value">
        {children}
      </Typography>
    </div>
  );
}

export interface ReadoutGroupProps {
  /** What these values are. */
  label: string;
  /** One Readout, or a grid of them. */
  children: ReactNode;
  testId?: string;
}

/**
 * A label and the values under it. Keeps its own alignment, so a group of
 * readouts reads left-to-right even in the middle of a centred page.
 */
export function ReadoutGroup({ label, children, testId }: ReadoutGroupProps) {
  return (
    <div className="readout-group" {...testIdAttr(testId)}>
      <Typography as="p" role="body/sm" className="readout-group__label">
        {label}
      </Typography>
      {children}
    </div>
  );
}
